import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { agenticAPI } from '../../../../services/api';

/**
 * Starting the agentic work over.
 *
 * Collapsed by default and last on the page, because it is the one control here
 * that destroys something. Opening it shows a PREVIEW first: the same counting
 * code the delete uses, so the number the operator agrees to is the number that
 * goes. Then the engagement's name, typed. A checkbox is a thing a tired
 * operator clicks through on the wrong engagement; a typed name is not.
 *
 * The panel is explicit about what survives, because "reset" reads like "throw
 * the engagement away" and it is not: the scope, the sensitivity flags, the
 * identity vault, the rules of engagement and the scanner findings all stay.
 *
 * WHY THIS EXISTS RATHER THAN AN "ABANDON" BUTTON: a hypothesis that is refuted
 * or abandoned still blocks a new one in its (target, class) pair, because
 * those are the settled statuses framing dedupes against. So abandoning a
 * hypothesis that was recorded wrongly would quietly suppress every future
 * hypothesis about that class on that host. Clearing is the honest way back.
 */

function Row({ label, value, tone }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`font-mono text-xs font-semibold ${tone || 'text-slate-800'}`}>
        {value}
      </span>
    </div>
  );
}

export default function ResetPanel({ pentestId, pentestName, onChanged }) {
  const [open, setOpen] = useState(false);
  const [deep, setDeep] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    if (!open) return;
    setError(null);
    try {
      setPreview(await agenticAPI.resetPreview(pentestId, deep));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not read what a reset would remove.');
    }
  }, [open, deep, pentestId]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await agenticAPI.reset(pentestId, confirm.trim(), deep);
      setDone(result);
      setConfirm('');
      await onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not reset the engagement.');
    } finally {
      setBusy(false);
    }
  };

  const expected = (pentestName || '').trim();
  const typed = confirm.trim();
  const matches = !!expected && typed === expected;
  const nothingToRemove = preview && preview.total === 0;

  // WHY THE BUTTON IS OFF, always. A disabled button with no explanation is
  // indistinguishable from a button that was pressed and did nothing, and that
  // is exactly how this failed in front of an operator: they typed the name,
  // clicked, believed the engagement was cleared, and it never was. The request
  // had never been made. Nothing in the panel said so.
  const blocked = nothingToRemove
    ? 'There is no agentic work on this engagement yet.'
    : !typed
      ? `Type ${expected || 'the engagement name'} above to enable this.`
      : !matches
        ? `That does not match. The engagement is called ${expected}.`
        : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" />
              : <ChevronRight className="h-4 w-4 text-slate-400" />}
        {/* THE HINT SITS NEXT TO THE LABEL, NOT AT THE FAR EDGE.
            `ml-auto` pushed "clears angles, runs and plans" to the right of a
            full-width row, so on a wide screen the label and the thing it
            warns about were a foot apart with nothing between them. The
            operator drew three arrows at that gap and said the element "looks
            out of place somehow". It was: two halves of one sentence, aligned
            to opposite walls. */}
        <span className="text-sm font-semibold text-slate-700">Start the agentic work over</span>
        <span className="text-[11px] text-slate-400">
          &mdash; clears every angle, run and plan on this engagement
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4">
          {done ? (
            <div className="rounded-md border border-wasabi-200 bg-wasabi-50 p-3 text-xs text-wasabi-800">
              <div className="font-semibold">Cleared.</div>
              <div className="mt-1">
                Removed {Object.entries(done.removed)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
                  .join(', ') || 'nothing'}.
              </div>
              <div className="mt-1 text-wasabi-700">
                Run the next step and the engagement gets a fresh Lead, which reads the
                scope and the findings again and drafts a new plan.
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-slate-600">
                Removes this engagement&apos;s agentic work so the Lead can start from a
                clean sheet. Use it when an angle was recorded wrongly: a refuted
                or abandoned angle still blocks a new one about the same class on
                the same host, so clearing is the way back rather than abandoning.
              </p>

              {error && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {preview && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-red-700">
                      Will be removed
                    </div>
                    {Object.entries(preview.removed).map(([k, n]) => (
                      <Row key={k} label={k.replace(/_/g, ' ')} value={n}
                           tone={n > 0 ? 'text-red-700' : 'text-slate-400'} />
                    ))}
                  </div>
                  <div className="rounded-md border border-slate-200 p-3">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Will be kept
                    </div>
                    {Object.entries(preview.kept).map(([k, n]) => (
                      <Row key={k} label={k.replace(/_/g, ' ')} value={n} />
                    ))}
                    <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                      Plus the scope and its sensitivity flags, the identity vault and
                      the rules of engagement. Resetting the agents is not throwing
                      away the engagement.
                    </p>
                  </div>
                </div>
              )}

              <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
                Also clear the scanner findings
                <span className="text-slate-400">
                  (they are what the Lead plans from; rarely what you want)
                </span>
              </label>

              <div className="mt-3">
                <label htmlFor="reset-confirm" className="text-xs font-semibold text-slate-700">
                  Type the engagement&apos;s name to confirm:{' '}
                  <span className="select-all font-mono font-normal text-slate-900">{expected}</span>
                </label>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <input
                    id="reset-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={pentestName}
                    className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:border-red-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    disabled={!matches || busy || nothingToRemove}
                    onClick={run}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                    Clear {preview ? preview.total : ''} row{preview?.total === 1 ? '' : 's'}
                  </button>
                </div>
                {blocked && (
                  <p className={`mt-1 text-[11px] ${typed && !matches ? 'text-amber-700' : 'text-slate-500'}`}>
                    {blocked}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-slate-500">
                  Irreversible, and audited before the work rather than after, so the
                  intent is on record even if the delete fails halfway.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
